import type { RouteLocationNormalized, RouteRecordRaw } from 'vue-router';
import ExamplePage from './pages/ExamplePage.vue';
import Dashboard from './pages/Dashboard.vue';
import AskMe from './pages/AskMe.vue';
import Library from './pages/Library.vue';
import Login from './pages/Login.vue';
import PreviewDashboard from './pages/PreviewDashboard.vue';
import PublicLink from './pages/PublicLink.vue';
import Interactions from './pages/Interactions.vue';
import Interaction from './pages/Interaction.vue';
import GeneralSettings from './pages/GeneralSettings.vue';
import Settings from './pages/Settings.vue';
import ManageCoachingForms from './pages/ManageCoachingForms.vue';
import ScheduleForms from './pages/ScheduleForms.vue';
import EditCoachingForm from './pages/EditCoachingForm.vue';
import QualityAssuranceInteractionDetail from './pages/QualityAssuranceInteractionDetail.vue';
import CreateCoachingForm from './pages/CreateCoachingForm.vue';
import Coaching from './pages/Coaching.vue';
import QualityAssuranceProtocolTable from './pages/QualityAssuranceProtocolTable.vue';
import ManageCoachingForm from './pages/ManageCoachingForm.vue';
import ManageUsers from './pages/ManageUsers.vue';
import ManageRoles from './pages/ManageRoles.vue';
import CreateRole from './pages/CreateRole.vue';
import EditRole from './pages/EditRole.vue';
import CreateUser from './pages/CreateUser.vue';
import EditUser from './pages/EditUser.vue';
import QualityAssuranceProtocolDetail from './pages/QualityAssuranceProtocolDetail.vue';
import OrganisationalResults from './pages/OrganisationalResults.vue';
import BackOffice from './back-office/pages/BackOffice.vue';
import BackOfficeOverview from './back-office/pages/BackOfficeOverview.vue';
import BackOfficeMigrateLegacyTenant from './back-office/pages/BackOfficeMigrateLegacyTenant.vue';
import BackOfficeActivateLegacyIntegration from './back-office/pages/BackOfficeActivateLegacyIntegration.vue';
import BackOfficeDataSeeding from './back-office/pages/BackOfficeDataSeeding.vue';
import BackOfficeImportTenantConfig from './back-office/pages/BackOfficeImportTenantConfig.vue';
import BackOfficeUpdateOrgTree from './back-office/pages/BackOfficeUpdateOrgTree.vue';
import BackOfficeImportAgents from './back-office/pages/BackOfficeImportAgents.vue';
import BackOfficeImportIntegrationConfig from './back-office/pages/BackOfficeImportIntegrationConfig.vue';
import BackOfficeDeleteTenant from './back-office/pages/BackOfficeDeleteTenant.vue';
import BackOfficeCreateTenant from './back-office/pages/BackOfficeCreateTenant.vue';
import BackOfficeStreamingConfig from './back-office/pages/BackOfficeStreamingConfig.vue';
import BackOfficeIntegrationConfig from './back-office/pages/BackOfficeIntegrationConfig.vue';
import BackOfficeGenesysOverview from './back-office/pages/BackOfficeGenesysOverview.vue';
import BackOfficeGenesysConfig from './back-office/pages/BackOfficeGenesysConfig.vue';
import BackOfficeGenesysSyncData from './back-office/pages/BackOfficeGenesysSyncData.vue';
import BackOfficeAsrConfig from './back-office/pages/BackOfficeAsrConfig.vue';
import BackOfficeCategories from './back-office/pages/BackOfficeCategories.vue';
import BackOfficeRetentionSettings from './back-office/pages/BackOfficeRetentionSettings.vue';
import BackOfficeKeyIndicateSyncSettings from './back-office/pages/BackOfficeKeyIndicateSyncSettings.vue';
import BackOfficeAsrAnalysisEnqueue from './back-office/pages/BackOfficeAsrAnalysisEnqueue.vue';
import BackOfficeSyncRawData from './back-office/pages/BackOfficeSyncRawData.vue';
import BackOfficeSyncCoachingData from './back-office/pages/BackOfficeSyncCoachingData.vue';
import BackOfficeSyncAiInsightsData from './back-office/pages/BackOfficeSyncAiInsightsData.vue';
import BackOfficeCsmUsers from './back-office/pages/BackOfficeCsmUsers.vue';
import BackOfficeSysopUsers from './back-office/pages/BackOfficeSysopUsers.vue';
import BackOfficeAgentConfig from './back-office/pages/BackOfficeAgentConfig.vue';
import BackOfficeAgentAnonymization from './back-office/pages/BackOfficeAgentAnonymization.vue';
import NotFound from './pages/NotFound.vue';
import SsoConnected from './pages/SsoConnected.vue';
import {
    permit,
    publicRoute,
    openRoute,
    protectedRoute,
    redirectRoute,
    roleRoute,
} from './utils/routePermissions';
import {
    PermissionArea,
    UserManagementPermissionFlag,
    RoleManagementPermissionFlag,
    QualityAssuranceModulePermissionFlag,
    InteractionModulePermissionFlag,
    DashboardModulePermissionFlag,
    AskMeModulePermissionFlag,
} from './consts/permissions';
import { API_URL } from '@/config';
import PublicLinkNotFound from './pages/PublicLinkNotFound.vue';
import { Role } from './composables/usePermissions';

const Area = PermissionArea;

export default [
    redirectRoute('/', '/workspace'),
    publicRoute('/login', Login),
    redirectRoute('/workspace', '/workspace/dashboard'),
    protectedRoute(
        '/workspace/dashboard',
        Dashboard,
        permit(Area.DashboardModule, DashboardModulePermissionFlag.ViewDashboards)
    ),
    openRoute('/workspace/report', ExamplePage),
    openRoute('/workspace/library', Library),
    publicRoute({
        path: '/workspace/preview-dashboard',
        component: PreviewDashboard,
        meta: { preview: true },
    }),
    protectedRoute(
        '/interactions',
        Interactions,
        permit(Area.InteractionModule, InteractionModulePermissionFlag.Access)
    ),
    protectedRoute('/ask-me', AskMe, permit(Area.AskMeModule, AskMeModulePermissionFlag.Access)),
    protectedRoute(
        '/ask-me/:chat',
        AskMe,
        permit(Area.AskMeModule, AskMeModulePermissionFlag.Access)
    ),
    openRoute('/top-drivers', ExamplePage),
    openRoute({
        path: '/settings',
        component: Settings,
        redirect: '/settings/general',
        children: [
            openRoute('general', GeneralSettings),
            protectedRoute(
                'manage-forms',
                ManageCoachingForms,
                permit(Area.QualityAssuranceModule, QualityAssuranceModulePermissionFlag.Access)
            ),
            protectedRoute(
                'manage-forms/:id',
                ManageCoachingForm,
                permit(Area.QualityAssuranceModule, QualityAssuranceModulePermissionFlag.Access)
            ),
            protectedRoute(
                'manage-forms/:id/edit',
                EditCoachingForm,
                permit(Area.QualityAssuranceModule, QualityAssuranceModulePermissionFlag.CreateForm)
            ),
            protectedRoute(
                'manage-forms/:id/copy',
                CreateCoachingForm,
                permit(Area.QualityAssuranceModule, QualityAssuranceModulePermissionFlag.CreateForm)
            ),
            protectedRoute(
                'schedule-forms',
                ScheduleForms,
                permit(
                    Area.QualityAssuranceModule,
                    QualityAssuranceModulePermissionFlag.ScheduleCoaching
                )
            ),
            protectedRoute(
                'manage-forms/new',
                CreateCoachingForm,
                permit(Area.QualityAssuranceModule, QualityAssuranceModulePermissionFlag.CreateForm)
            ),
            protectedRoute(
                'manage-users',
                ManageUsers,
                permit(Area.UserManagement, UserManagementPermissionFlag.Access)
            ),
            protectedRoute(
                'manage-users/new',
                CreateUser,
                permit(Area.UserManagement, UserManagementPermissionFlag.Access)
            ),
            protectedRoute(
                'manage-users/:id/edit',
                EditUser,
                permit(Area.UserManagement, UserManagementPermissionFlag.Access)
            ),
            protectedRoute(
                'manage-roles',
                ManageRoles,
                permit(Area.RoleManagement, RoleManagementPermissionFlag.Access)
            ),
            protectedRoute(
                'manage-roles/new',
                CreateRole,
                permit(Area.RoleManagement, RoleManagementPermissionFlag.Access)
            ),
            protectedRoute(
                'manage-roles/:id/edit',
                EditRole,
                permit(Area.RoleManagement, RoleManagementPermissionFlag.Access)
            ),
        ],
    }),
    roleRoute(
        {
            path: '/back-office',
            component: BackOffice,
            redirect: '/back-office/overview',
            children: [
                roleRoute('overview', BackOfficeOverview, Role.SYSOP),
                roleRoute('migrate-legacy-tenant', BackOfficeMigrateLegacyTenant, Role.SYSOP),
                roleRoute(
                    'migrate-legacy-integration',
                    BackOfficeActivateLegacyIntegration,
                    Role.SYSOP
                ),
                roleRoute('data-seeding', BackOfficeDataSeeding, Role.SYSOP),
                roleRoute('import-tenant-config', BackOfficeImportTenantConfig, Role.SYSOP),
                roleRoute('update-org-tree', BackOfficeUpdateOrgTree, Role.SYSOP),
                roleRoute('import-agents', BackOfficeImportAgents, Role.SYSOP),
                roleRoute(
                    'import-integration-config',
                    BackOfficeImportIntegrationConfig,
                    Role.SYSOP
                ),
                roleRoute('streaming-config', BackOfficeStreamingConfig, Role.SYSOP),
                roleRoute('integration-config', BackOfficeIntegrationConfig, Role.SYSOP),
                roleRoute('genesys-overview', BackOfficeGenesysOverview, Role.SYSOP),
                roleRoute('genesys-config', BackOfficeGenesysConfig, Role.SYSOP),
                roleRoute('genesys-sync-data', BackOfficeGenesysSyncData, Role.SYSOP),
                roleRoute('asr-config', BackOfficeAsrConfig, Role.SYSOP),
                roleRoute('categories', BackOfficeCategories, Role.SYSOP),
                roleRoute('retention-settings', BackOfficeRetentionSettings, Role.SYSOP),
                roleRoute(
                    'key-indicate-sync-settings',
                    BackOfficeKeyIndicateSyncSettings,
                    Role.SYSOP
                ),
                roleRoute('asr-enqueue', BackOfficeAsrAnalysisEnqueue, Role.SYSOP),
                roleRoute('delete-tenant', BackOfficeDeleteTenant, Role.SYSOP),
                roleRoute('create-tenant', BackOfficeCreateTenant, Role.SYSOP),
                roleRoute('sync-raw-data', BackOfficeSyncRawData, Role.SYSOP),
                roleRoute('sync-coaching-data', BackOfficeSyncCoachingData, Role.SYSOP),
                roleRoute('sync-ai-insights-data', BackOfficeSyncAiInsightsData, Role.SYSOP),
                roleRoute('internal-users/csm', BackOfficeCsmUsers, Role.SYSOP),
                roleRoute('internal-users/sysop', BackOfficeSysopUsers, Role.SYSOP),
                roleRoute('agent-config', BackOfficeAgentConfig, Role.SYSOP),
                roleRoute('agent-anonymization', BackOfficeAgentAnonymization, Role.SYSOP),
            ],
        },
        Role.SYSOP
    ),
    publicRoute('/share', PublicLink),
    publicRoute('/share-404', PublicLinkNotFound),
    publicRoute('/sso/microsoft/connected', SsoConnected),
    publicRoute({
        path: '/s/:shortCode',
        beforeEnter(to: RouteLocationNormalized) {
            const publicApiUrl = API_URL.replace('/api/echo', '');
            window.location.href = `${publicApiUrl}/s/${to.params.shortCode}`;
            return true;
        },
        component: { render: () => null },
    } as RouteRecordRaw),
    protectedRoute(
        '/interaction',
        Interaction,
        permit(Area.InteractionModule, InteractionModulePermissionFlag.Access)
    ),
    protectedRoute(
        '/interaction/:interactionId',
        Interaction,
        permit(Area.InteractionModule, InteractionModulePermissionFlag.Access)
    ),
    redirectRoute('/quality-assurance', '/quality-assurance/protocols'),
    protectedRoute(
        '/quality-assurance/protocols',
        QualityAssuranceProtocolTable,
        permit(Area.QualityAssuranceModule, QualityAssuranceModulePermissionFlag.Access)
    ),
    protectedRoute(
        '/quality-assurance/protocols/:protocolId',
        QualityAssuranceProtocolDetail,
        permit(Area.QualityAssuranceModule, QualityAssuranceModulePermissionFlag.Access)
    ),
    protectedRoute(
        '/quality-assurance/coaching',
        Coaching,
        permit(Area.QualityAssuranceModule, QualityAssuranceModulePermissionFlag.ManualCoaching)
    ),
    protectedRoute(
        '/quality-assurance/:interactionId',
        QualityAssuranceInteractionDetail,
        permit(Area.QualityAssuranceModule, QualityAssuranceModulePermissionFlag.Access)
    ),
    protectedRoute(
        '/quality-assurance/organisational-results',
        OrganisationalResults,
        permit(Area.QualityAssuranceModule, QualityAssuranceModulePermissionFlag.Access)
    ),

    openRoute('/:pathMatch(.*)*', NotFound),
];
